import { ErrorMessage } from '@hookform/error-message';
import { cn } from '@workspace/utils';
import { Controller, FieldPath, FieldValues, get, RegisterOptions, useFormContext } from 'react-hook-form';
import type { SelectOption } from '../../types';
import { Field, Label, LabelProps, Radio, RadioProps, TextError } from '../form';

export interface FieldRadioGroupProps<TFormValues extends FieldValues = any> 
  extends RadioProps, LabelProps {
  name: FieldPath<TFormValues>;
  validation?: RegisterOptions<TFormValues, any>;
  options: SelectOption[];
  type?: 'small' | 'large';
  onClick?: () => void;
  col?: 1 | 2 | 3;
  classNameGroup?: string;
  center?: boolean;
}

const GRID_COLS = {
  1: 'grid-cols-1 gap-1.5',
  2: 'grid-cols-2 gap-3',
  3: 'grid-cols-3 gap-3',
} as const;

/**
 * Groupe de radio buttons connecté au formulaire
 * - Grille responsive configurable (1-3 colonnes)
 * - Validation automatique et gestion d'état
 * - Support des icônes et callbacks personnalisés
 */
export function FieldRadioGroup({
  label,
  name,
  required = true,
  options,
  children,
  validation,
  type = 'large',
  className,
  classNameGroup,
  onClick,
  col = 2,
  disabled,
  center = false,
}: FieldRadioGroupProps) {
  const methods = useFormContext<any>();

  const validate = validation?.validate 
    ? typeof validation.validate === 'function'
      ? { validate: validation.validate }
      : validation.validate
    : {};

  return (
    <Field className={className}>
      <Label required={required}>{label}</Label>
      <div className={cn(
        'grid w-full', 
        center ? 'justify-items-center' : 'justify-items-start', 
        GRID_COLS[col], 
        classNameGroup
      )}>
        {options.map((option, index) => (
          <Controller
            control={methods.control}
            key={index}
            name={name}
            render={({ field: { onChange, value, ...field } }) => (
              <Radio
                checked={value === option.value}
                label={option.label}
                icon={option.icon}
                onChange={() => {
                  if (!disabled && onChange) {
                    option.onChange?.(option.value);
                    onChange(option.value);
                  }
                }}
                onClick={() => !disabled && onClick?.()}
                type={type}
                value={option.value?.toString() ?? ''}
                error={get(methods.formState.errors, name) !== undefined}
                disabled={methods.formState.isSubmitting || option.disabled || disabled}
                description={option.description}
                {...field}
              />
            )}
            rules={{
              ...validation,
              validate: {
                isRequired: (value) => {
                  // why: une valeur vide ('' venant de l'état initial) ne compte pas comme une réponse
                  if (required && (value === undefined || value === null || value === '')) {
                    return 'Ce champs est requis';
                  }
                  return true;
                },
                ...validate,
              },
            }}
          />
        ))}
      </div>
      <ErrorMessage
        errors={methods.formState.errors}
        name={name}
        render={({ message }) => <TextError className="text-center">{message}</TextError>}
      />
      {children}
    </Field>
  );
}
